import { parseApiDate } from '@/lib/dates';

/** Mirrors App\Http\Resources\BackOffice\PromoCodeResource in kashround-api. */
export type PromoDiscountType = 'percentage' | 'free_period' | 'fixed';

export type PromoCodeStatus = 'active' | 'inactive' | 'expired' | 'fully_redeemed';

export interface PromoCode {
  id: number;
  code: string;
  discountType: PromoDiscountType;
  discountValue: number;
  /** null means no limit. */
  maxRedemptions: number | null;
  redemptionsCount: number;
  /** YYYY-MM-DD. The code is valid through the end of this day. null means it never expires. */
  expiresOn: string | null;
  isActive: boolean;
  status: PromoCodeStatus;
  isRedeemed: boolean;
  /** Subscriptions that chose this code at checkout and will redeem it when they pay. */
  pendingCount: number;
  /** Request field names the API will refuse to change. */
  lockedFields: string[];
}

export const PROMO_STATUS_LABELS: Record<PromoCodeStatus, string> = {
  active: 'Active',
  inactive: 'Inactive',
  expired: 'Expired',
  fully_redeemed: 'Fully redeemed',
};

/** What a code gives, in the words staff would use with a group. */
export function describeDiscount(code: Pick<PromoCode, 'discountType' | 'discountValue'>): string {
  if (code.discountType === 'free_period') {
    return `${code.discountValue} free ${code.discountValue === 1 ? 'month' : 'months'}`;
  }
  if (code.discountType === 'percentage') {
    return `${code.discountValue}% off every invoice`;
  }
  // No fixed-amount code can be created here; one made another way has no
  // agreed currency, so the number is shown without one.
  return `${code.discountValue} off every invoice`;
}

export function describeUse(code: Pick<PromoCode, 'redemptionsCount' | 'maxRedemptions'>): string {
  return code.maxRedemptions === null
    ? `${code.redemptionsCount}, no limit`
    : `${code.redemptionsCount} of ${code.maxRedemptions}`;
}

/** YYYY-MM-DD to "Dec 31, 2026", built from its parts so no timezone can move the day. */
export function formatExpiry(expiresOn: string | null): string {
  if (!expiresOn) return 'No expiry';
  return parseApiDate(expiresOn).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
